import { createFileRoute, Navigate, useNavigate } from '@tanstack/react-router'
import * as v from 'valibot'
import { useSessionUser } from '@/hooks/use-session'
import { useMyEmployeeProfile } from '@/hooks/use-session'
import { pickIncompleteTestPath } from '@/main-helpers'
import { SocialButtons } from '@/routes/_app/-components/social-buttons'
import { DeleteAccount } from './-components/shared/delete-account'
import { SettingsSection } from './-components/settings/settings-section'
import { MyEmployersSection } from './-components/settings/my-employers-section'
import { useLingui } from '@lingui/react/macro'
import { Avatar } from './-components/shared/avatar'
import { fileToBase64 } from '@/components/custom/form'
import { useAppForm } from '@/lib/use-app-form'
import { noop, useMutation, useSuspenseQuery } from '@tanstack/react-query'
import { trpc } from '@/main'
import { Button } from '@/components/ui/button'
import {
  CameraIcon,
  UserIcon,
  BriefcaseIcon,
  AlertTriangleIcon,
  BuildingIcon,
  PhoneIcon,
  MapPinIcon,
  MailIcon,
  StarIcon,
} from 'lucide-react'
import { SparklesIcon } from '@/routes/_app/-components/shared/navigation-config'
import { useRef, useState, Suspense } from 'react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useActiveEmployer } from '@/hooks/use-active-employer'
import { usePermissions } from '@/hooks/use-permissions'
import { FormReadOnlyProvider } from '@/components/custom/form/shared'
import { ImageEditor } from '@/components/custom/form/-components/image-editor'
import { Collapse } from '@/components/custom/collapse'
import { useLists } from '@/hooks/use-lists'
import { toast } from '@/lib/toast'
import { useAppStore } from '@/lib/app-store'
import { revalidateLogic } from '@tanstack/react-form'
import { jobInfoSchema, personalInfoSchema } from 'shared/applicant-form'
import { SettingsContentSkeleton } from './-components/app-layout-skeleton'
import { ButtonPrimitive } from '@/components/ui/button-primitive'

export const Route = createFileRoute('/_app/account/')({
  context: () => ({
    companyProfileQueryOptions: trpc.companies.getCompanyProfile.queryOptions(),
    myTeamsQueryOptions: trpc.employees.getMyTeams.queryOptions(),
    myAssignedJobsQueryOptions: trpc.employees.getMyAssignedJobs.queryOptions(),
    adminEmployeesQueryOptions: trpc.employees.getAdminEmployees.queryOptions(),
  }),
  loader: ({ context }) => {
    context.queryClient.query(context.companyProfileQueryOptions).catch(noop)
    context.queryClient.query(context.myTeamsQueryOptions).catch(noop)
    context.queryClient.query(context.myAssignedJobsQueryOptions).catch(noop)
    context.queryClient.query(context.adminEmployeesQueryOptions).catch(noop)
  },
  component: RouteComponent,
  pendingComponent: SettingsContentSkeleton,
})

function RouteComponent() {
  const user = useSessionUser()
  const { activeEmployerId, isEmployee } = useActiveEmployer()
  const { isCompanyView } = usePermissions()

  // An admin/authorized employee acting AS the company (company view) gets the COMPANY settings
  // ("Configuración"), not their own employee profile — editable by admins, read-only for authorized.
  if (isEmployee && activeEmployerId !== null && isCompanyView) return <CompanyProfileAsActing />

  // When the active employer is a company (not D'Anchiano), show read-only profile.
  // When D'Anchiano is selected (activeEmployerId === null), the employee can edit their own data.
  if (isEmployee && activeEmployerId !== null) return <EmployeeProfileReadOnly />

  return user?.type === 'applicant' ? <UpdateApplicant /> : user?.type === 'company' ? <UpdateCompany /> : null
}

// Company settings ("Configuración") for an admin/authorized employee acting AS the company (Block C).
// Bounded to Company Information + Contact — the core company config — sourced from and written to the
// ACTING company (server-resolved, IDOR-safe). Editable by admins (employees:write); read-only for
// authorized (the whole form is wrapped in FormReadOnlyProvider and the Save buttons are hidden).
function CompanyProfileAsActing() {
  const { t } = useLingui()
  const { can } = usePermissions()
  const canWrite = can('employees:write')
  const lists = useLists()
  const { data: company } = useSuspenseQuery(Route.useRouteContext().companyProfileQueryOptions)
  const { mutateAsync: updateCompanyProfile } = useMutation(trpc.companies.updateCompanyProfile.mutationOptions())

  const companyForm = useAppForm({
    validators: {
      onChange: v.object({
        name: v.pipe(v.string(), v.minLength(1)),
        country: v.pipe(v.string(), v.minLength(1)),
        province: v.pipe(v.string(), v.minLength(1)),
        city: v.pipe(v.string(), v.minLength(1)),
        cp: v.string(),
        employees: v.nullable(v.number()),
        sector: v.nullable(v.number()),
      }),
    },
    defaultValues: {
      // A freshly registered company keeps its name in the `company` column; UpdateCompany moves it to
      // `name` (firstname) on the first save. Coalesce so the acting admin always sees the real name.
      name: company?.name ?? company?.company ?? '',
      country: company?.country ?? '',
      province: company?.province ?? '',
      city: company?.city ?? '',
      cp: company?.cp ?? '',
      employees: company?.employees ?? null,
      sector: company?.sector ?? null,
    },
    onSubmit: async ({ value }) => {
      await updateCompanyProfile(value)
      toast.success(t`Saved successfully`)
    },
  })

  const contactForm = useAppForm({
    validators: {
      onChange: v.object({
        contactFirstname: v.pipe(v.string(), v.minLength(1)),
        contactLastname: v.pipe(v.string(), v.minLength(1)),
        contactPhone: v.pipe(v.string(), v.minLength(1)),
      }),
    },
    defaultValues: {
      contactFirstname: company?.contactFirstname ?? '',
      contactLastname: company?.contactLastname ?? '',
      contactPhone: company?.contactPhone ?? '',
    },
    onSubmit: async ({ value }) => {
      await updateCompanyProfile(value)
      toast.success(t`Saved successfully`)
    },
  })

  return (
    <FormReadOnlyProvider value={!canWrite}>
      <div className="flex flex-col gap-4">
        <SettingsSection
          title={t`Company information`}
          description={t`Basic information about your company`}
          icon={BuildingIcon}
          compact
        >
          <form
            onSubmit={(e) => {
              e.preventDefault()
              companyForm.handleSubmit()
            }}
            className="flex flex-col gap-2"
          >
            <companyForm.AppField
              name="name"
              children={(field) => <field.TextField label={t`Company name`} orientation="horizontal" />}
            />
            <companyForm.AppField
              name="country"
              listeners={{ onChange: () => companyForm.setFieldValue('province', '') }}
              children={(field) => <field.CountryField label={t`Country`} orientation="horizontal" />}
            />
            <companyForm.Subscribe selector={(state) => state.values.country}>
              {(country) => (
                <companyForm.AppField
                  name="province"
                  children={(field) => (
                    <field.ProvinceField label={t`Province / State`} orientation="horizontal" country={country} />
                  )}
                />
              )}
            </companyForm.Subscribe>
            <companyForm.AppField
              name="city"
              children={(field) => <field.TextField label={t`City`} orientation="horizontal" />}
            />
            <companyForm.AppField
              name="cp"
              children={(field) => <field.TextField label={t`Postal code`} orientation="horizontal" />}
            />
            <companyForm.AppField
              name="employees"
              children={(field) => (
                <field.SimpleSelectNumberField
                  label={t`Number of employees`}
                  options={lists.employees.map((label, value) => ({ value, label }))}
                  orientation="horizontal"
                />
              )}
            />
            <companyForm.AppField
              name="sector"
              children={(field) => (
                <field.SelectNumberField
                  label={t`Sector`}
                  options={lists.sector.map((label, value) => ({ value, label }))}
                  orientation="horizontal"
                />
              )}
            />
            {canWrite && (
              <div className="mt-2 flex">
                <companyForm.AppForm>
                  <companyForm.SubmitButton label={t`Save changes`} />
                </companyForm.AppForm>
              </div>
            )}
          </form>
        </SettingsSection>

        <SettingsSection title={t`Contact information`} description={t`How we can reach you`} icon={PhoneIcon} compact>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              contactForm.handleSubmit()
            }}
            className="flex flex-col gap-2"
          >
            <contactForm.AppField
              name="contactFirstname"
              children={(field) => <field.TextField label={t`Contact first name`} orientation="horizontal" />}
            />
            <contactForm.AppField
              name="contactLastname"
              children={(field) => <field.TextField label={t`Contact last name`} orientation="horizontal" />}
            />
            <contactForm.AppField
              name="contactPhone"
              children={(field) => <field.TextField label={t`Contact phone number`} orientation="horizontal" />}
            />
            {canWrite && (
              <div className="mt-2 flex">
                <contactForm.AppForm>
                  <contactForm.SubmitButton label={t`Save changes`} />
                </contactForm.AppForm>
              </div>
            )}
          </form>
        </SettingsSection>
      </div>
    </FormReadOnlyProvider>
  )
}

// Read-only "My profile data" for an employee registered by a company.
// Mirrors the company's "Add employee" form (same sections, icons, labels, order) but static —
// the employee can view but not modify their data, and the Permissions section is not shown.
function ReadOnlyRow(props: { label: string; value: React.ReactNode }) {
  const { t } = useLingui()
  return (
    <div className="flex items-center justify-between gap-4 py-1.5">
      <span className="text-muted-foreground text-sm">{props.label}</span>
      <span className="text-right text-sm font-medium">
        {props.value === '' || props.value == null ? (
          <span className="text-muted-foreground">{t`Not provided`}</span>
        ) : (
          props.value
        )}
      </span>
    </div>
  )
}

// Renders only while an employer is the ACTIVE context, so the session-resolved no-arg endpoints below
// all scope to that same employer — no companyId threading needed.
function EmployeeProfileReadOnly() {
  const { t } = useLingui()
  const user = useSessionUser()
  const navigate = useNavigate()
  const lists = useLists()
  // The employee's profile as stored by the active company (session-resolved by the no-arg form — the
  // same cache entry the _app loader prefetches and the topbar user-menu reads).
  const profile = useMyEmployeeProfile()

  // Show the employee's data exactly as the company stores it — no fallback to the person's own
  // account. The company captures this data (copied from the applicant) when adding the employee.
  // The teams the employee actually belongs to within this employer (not every team the company
  // owns) and the position(s) they're assigned to — both scoped to the active employer so an
  // employee of several companies never sees another employer's data mixed in.
  const { data: teams } = useSuspenseQuery(Route.useRouteContext().myTeamsQueryOptions)
  const { data: assignedJobsResult } = useSuspenseQuery(Route.useRouteContext().myAssignedJobsQueryOptions)

  // Value-shaped test gate: an incomplete-test employee must finish the questionnaires before their
  // employer-scoped profile means anything — send them to the next unfinished one (the same landing the
  // old TEST_INCOMPLETE error redirect produced, now explicit instead of a side effect of reading).
  if (assignedJobsResult.status !== 'ok') return <Navigate to={pickIncompleteTestPath(user)} />
  const assignedJobs = assignedJobsResult.list

  const sexLabel =
    profile?.sex === 'male' ? t`Man` : profile?.sex === 'female' ? t`Woman` : profile?.sex === 'neutral' ? t`Other` : ''
  const countryLabel = profile?.countryName ?? ''
  const provinceLabel = profile?.provinceName ?? ''
  const levelLabel = profile?.level != null ? lists.level[profile?.sex ?? 'neutral']?.[profile.level] : ''
  const functionLabel = profile?.function != null ? lists.function[profile.function] : ''
  const sectorLabel = profile?.sector != null ? lists.sector[profile.sector] : ''
  const educationLabel = profile?.educationalLevel != null ? lists.educationalLevel[profile.educationalLevel] : ''
  const teamNames = teams.flatMap((tm) => (tm.name ? [tm.name] : [])).join(', ')
  const puestoNames = assignedJobs.flatMap((j) => (j.name ? [j.name] : [])).join(', ')
  // Stored as YYYY-MM-DD; show day/month/year (Spanish) instead of the raw ISO order.
  const birthdateLabel = profile?.birthdate ? profile.birthdate.slice(0, 10).split('-').reverse().join('/') : ''

  return (
    <div className="flex flex-col gap-4">
      <SettingsSection title={t`Personal information`} icon={UserIcon} compact>
        <div className="flex flex-col">
          <div className="flex items-center justify-between gap-4 py-1.5">
            <span className="text-muted-foreground text-sm">{t`Photo`}</span>
            <Avatar
              size="xl"
              url={profile?.image}
              type={user?.type}
              name={`${profile?.firstname ?? ''} ${profile?.lastname ?? ''}`}
            />
          </div>
          <ReadOnlyRow label={t`Email`} value={profile?.email} />
          <ReadOnlyRow label={t`First name`} value={profile?.firstname} />
          <ReadOnlyRow label={t`Last name`} value={profile?.lastname} />
          <ReadOnlyRow label={t`Sex`} value={sexLabel} />
          <ReadOnlyRow label={t`Date of birth`} value={birthdateLabel} />
        </div>
      </SettingsSection>

      <SettingsSection title={t`Organization`} icon={BuildingIcon} compact>
        <div className="flex flex-col">
          {/* "Puesto" = the position(s) the company assigned, not the free-text job title (which also
              renders as "Puesto" in Spanish and was the source of the spurious "No indicado"). */}
          <ReadOnlyRow label={t`Position`} value={puestoNames} />
          <ReadOnlyRow label={t`Teams`} value={teamNames} />
        </div>
      </SettingsSection>

      <SettingsSection title={t`Professional details`} icon={BriefcaseIcon} compact>
        <div className="flex flex-col">
          <ReadOnlyRow label={t`Professional level`} value={levelLabel} />
          <ReadOnlyRow label={t`Function`} value={functionLabel} />
          <ReadOnlyRow label={t`Sector`} value={sectorLabel} />
          <ReadOnlyRow label={t`Studies`} value={educationLabel} />
          <ReadOnlyRow label={t`With people under charge?`} value={profile?.peopleInCharge ? t`Yes` : t`No`} />
        </div>
      </SettingsSection>

      <SettingsSection title={t`Location`} icon={MapPinIcon} compact>
        <div className="flex flex-col">
          <ReadOnlyRow label={t`Country`} value={countryLabel} />
          <ReadOnlyRow label={t`Province / State`} value={provinceLabel} />
          <ReadOnlyRow label={t`City`} value={profile?.city} />
          <ReadOnlyRow label={t`Postal code`} value={profile?.cp} />
        </div>
      </SettingsSection>

      <div className="flex">
        <Button type="button" onClick={() => navigate({ to: '/report' })}>
          {t`Accept`}
        </Button>
      </div>
    </div>
  )
}

function ProfileHeaderWithSocial(props: { onImageChange: (file: File) => void }) {
  const { t } = useLingui()
  const user = useSessionUser()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [pendingFile, setPendingFile] = useState<File | null>(null)

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    // Reset input so re-selecting the same file triggers onChange
    event.target.value = ''

    const img = new Image()
    const objectUrl = URL.createObjectURL(file)
    img.addEventListener('load', () => {
      URL.revokeObjectURL(objectUrl)
      const w = img.width
      const h = img.height
      if (w < 256 || h < 256) {
        toast.error(t`Image too small`, {
          description: t`Image must be at least 256x256 pixels. Uploaded image is ${w}x${h} pixels.`,
        })
        return
      }
      setPendingFile(file)
      setIsEditing(true)
    })
    img.addEventListener('error', () => {
      URL.revokeObjectURL(objectUrl)
      toast.error(t`Invalid image`, { description: t`Failed to load image. Please try a different file.` })
    })
    img.src = objectUrl
  }

  return (
    <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start sm:gap-6">
      <div className="flex shrink-0 flex-col items-center gap-2">
        <ButtonPrimitive
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="group relative cursor-pointer overflow-hidden rounded-lg"
        >
          <Avatar size="4xl" url={user?.image} type={user?.type} name={user?.name} />
          <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/0 transition-colors group-hover:bg-black/50">
            <CameraIcon className="size-5 text-white opacity-0 transition-opacity group-hover:opacity-100" />
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
        </ButtonPrimitive>
        <div className="text-center">
          <h2 className="text-base font-semibold">{user?.name || t`Your Name`}</h2>
          <p className="text-muted-foreground text-xs">{user?.email}</p>
        </div>
      </div>

      <div className="bg-border hidden h-auto w-px self-stretch sm:block" />
      <div className="bg-border h-px w-full sm:hidden" />

      <div className="flex flex-col gap-2">
        <p className="text-muted-foreground text-xs font-medium">{t`Connected accounts`}</p>
        <SocialButtons />
      </div>

      <ImageEditor
        open={isEditing}
        file={pendingFile}
        aspectRatio={{ width: 128, height: 128 }}
        onSave={(croppedFile) => {
          props.onImageChange(croppedFile)
          setIsEditing(false)
          setPendingFile(null)
        }}
        onOpenChange={(open) => {
          if (!open) {
            setIsEditing(false)
            setPendingFile(null)
          }
        }}
      />
    </div>
  )
}

function ChangeEmailSection() {
  const { t } = useLingui()
  const user = useSessionUser()
  const { mutateAsync: changeEmail } = useMutation(trpc.auth.changeEmail.mutationOptions())

  const currentEmail = user?.email?.toLowerCase() ?? ''

  const emailForm = useAppForm({
    defaultValues: {
      email: user?.email ?? '',
    },
    validators: {
      onChange: v.object({
        email: v.pipe(
          v.string(),
          v.email(),
          v.check((value) => value.toLowerCase() !== currentEmail, t`Email must be different from your current one`),
        ),
      }),
    },
    onSubmit: async ({ value }) => {
      try {
        await changeEmail({ email: value.email })
        toast.success(t`We've sent a verification link to your new email address. Please check your inbox.`)
      } catch {
        // Errors are surfaced (and translated via their machine code, e.g. EMAIL_IN_USE) by the global
        // tRPC error handler in main.tsx — no per-call message matching needed here.
      }
    },
  })

  return (
    <SettingsSection title={t`Email`} description={t`Your login email address`} icon={MailIcon} compact>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          emailForm.handleSubmit()
        }}
        className="flex flex-col gap-2"
      >
        <emailForm.AppField
          name="email"
          children={(field) => <field.TextField label={t`Email`} orientation="horizontal" />}
        />
        <div className="mt-2 flex">
          <emailForm.AppForm>
            <emailForm.SubmitButton label={t`Change email`} />
          </emailForm.AppForm>
        </div>
      </form>
    </SettingsSection>
  )
}

// Applicant Profile
// Fields, labels, order and mechanics mirror the registration flow
// (apps/.../applicants/applicant-form-section.tsx): About → Location → Career.
function UpdateApplicant() {
  const { t } = useLingui()
  const user = useSessionUser()
  const { mutateAsync: updateUser } = useMutation(trpc.auth.updateUser.mutationOptions())
  const lists = useLists()

  const personalForm = useAppForm({
    // Same validation as the registration "About + Location" steps so required fields show the "*"
    // marker and "Requerido" message, and a blank required field blocks save. `image` is handled
    // separately by the header avatar, so it's omitted from the schema here.
    validationLogic: revalidateLogic(),
    validators: { onDynamic: v.omit(personalInfoSchema, ['image']) },
    defaultValues: {
      firstname: user?.firstname ?? '',
      lastname: user?.lastname ?? '',
      birthdate: user?.birthdate ?? '',
      sex: user?.sex,
      country: user?.country ?? '',
      province: user?.province ?? '',
      city: user?.city ?? '',
      cp: user?.cp ?? '',
    },
    onSubmit: async ({ value }) => {
      await updateUser(value)
      toast.success(t`Saved successfully`)
    },
  })

  const jobForm = useAppForm({
    // Same validation as the registration "Career" step (jobInfoSchema), which nests company/
    // employees under `work` and only requires them when `workedPreviously` is set — so toggling the
    // switch re-checks the conditionally-required fields, just like registration.
    validationLogic: revalidateLogic(),
    validators: { onDynamic: jobInfoSchema },
    defaultValues: {
      work: {
        // Transient UI flag (not persisted) driving the conditional fields, same as registration. Derived from
        // existing data: an already-employed professional has a company/employees on file.
        workedPreviously: user?.company != null || user?.employees != null,
        company: user?.company ?? null,
        employees: user?.employees ?? null,
      },
      job: user?.job ?? '',
      educationalLevel: user?.educationalLevel ?? null,
      level: user?.level ?? null,
      function: user?.function ?? null,
      sector: user?.sector ?? null,
    },
    onSubmit: async ({ value }) => {
      const { work, ...rest } = value
      await updateUser({
        ...rest,
        company: work.workedPreviously ? work.company : null,
        employees: work.workedPreviously ? work.employees : null,
      })
      toast.success(t`Saved successfully`)
    },
  })

  const sexOptions = [
    { value: 'male', label: t`Man` },
    { value: 'female', label: t`Woman` },
    { value: 'neutral', label: t`Other` },
  ]

  const handleImageChange = async (file: File) => {
    await updateUser({ image: await fileToBase64(file) })
  }

  return (
    <div className="flex flex-col gap-4">
      <SettingsSection compact className="self-center">
        <ProfileHeaderWithSocial onImageChange={handleImageChange} />
      </SettingsSection>

      <ChangeEmailSection />

      <SettingsSection
        title={t`Personal information`}
        description={t`Helps us personalize your report`}
        icon={UserIcon}
        compact
      >
        <form
          onSubmit={(e) => {
            e.preventDefault()
            personalForm.handleSubmit()
          }}
          className="flex flex-col gap-2"
        >
          <personalForm.AppField
            name="firstname"
            children={(field) => <field.TextField label={t`First name`} orientation="horizontal" />}
          />
          <personalForm.AppField
            name="lastname"
            children={(field) => <field.TextField label={t`Last name`} orientation="horizontal" />}
          />
          <personalForm.AppField
            name="birthdate"
            children={(field) => <field.DateField label={t`Date of birth`} orientation="horizontal" />}
          />
          <personalForm.AppField
            name="sex"
            children={(field) => (
              <field.SimpleSelectField label={t`Sex`} options={sexOptions} orientation="horizontal" />
            )}
          />
          <personalForm.AppField
            name="country"
            listeners={{ onChange: () => personalForm.setFieldValue('province', '') }}
            children={(field) => <field.CountryField label={t`Country`} orientation="horizontal" />}
          />
          <personalForm.Subscribe selector={(state) => state.values.country}>
            {(country) => (
              <personalForm.AppField
                name="province"
                children={(field) => (
                  <field.ProvinceField label={t`Province / State`} orientation="horizontal" country={country} />
                )}
              />
            )}
          </personalForm.Subscribe>
          <personalForm.AppField
            name="city"
            children={(field) => <field.TextField label={t`City`} orientation="horizontal" />}
          />
          <personalForm.AppField
            name="cp"
            children={(field) => <field.TextField label={t`Postal code`} orientation="horizontal" />}
          />

          <div className="mt-2 flex">
            <personalForm.AppForm>
              <personalForm.SubmitButton label={t`Save changes`} />
            </personalForm.AppForm>
          </div>
        </form>
      </SettingsSection>

      <SettingsSection
        title={t`Professional information`}
        description={t`Identify your profile in the market`}
        icon={BriefcaseIcon}
        compact
      >
        <form
          onSubmit={(e) => {
            e.preventDefault()
            jobForm.handleSubmit()
          }}
          className="flex flex-col gap-2"
        >
          <jobForm.Subscribe selector={(state) => state.values.work.workedPreviously}>
            {(workedPreviously) => (
              <>
                {/* Switch + company fields are one flex child so collapsing leaves a single
                    gap to the next field, not a doubled, empty one. */}
                <div className="flex flex-col">
                  <jobForm.AppField
                    name="work.workedPreviously"
                    listeners={{
                      onChange: ({ value: hasWorked }) => {
                        const level = jobForm.getFieldValue('level')
                        if (!hasWorked && level && level > 2) {
                          jobForm.resetField('level')
                        }
                      },
                    }}
                    children={(field) => <field.SwitchField label={t`I have worked before`} orientation="horizontal" />}
                  />

                  <Collapse open={workedPreviously}>
                    {/* pt-2 is the gap to the switch — kept inside so it collapses too */}
                    <div className="flex flex-col gap-2 pt-2">
                      <jobForm.AppField
                        name="work.company"
                        children={(field) => (
                          <field.TextField label={t`Company name`} orientation="horizontal" optional={false} />
                        )}
                      />
                      <jobForm.AppField
                        name="work.employees"
                        children={(field) => (
                          <field.SelectNumberField
                            label={t`Company employees`}
                            options={lists.employees.map((label, value) => ({ value, label }))}
                            orientation="horizontal"
                            optional={false}
                          />
                        )}
                      />
                    </div>
                  </Collapse>
                </div>

                <jobForm.AppField
                  name="job"
                  children={(field) => (
                    <field.TextField
                      label={workedPreviously ? t`Job title` : t`Preferred position`}
                      orientation="horizontal"
                    />
                  )}
                />

                <jobForm.AppField
                  name="educationalLevel"
                  children={(field) => (
                    <field.SimpleSelectNumberField
                      label={t`Studies`}
                      options={lists.educationalLevel.map((label, value) => ({ value, label }))}
                      orientation="horizontal"
                    />
                  )}
                />
                <jobForm.AppField
                  name="level"
                  children={(field) => (
                    <field.SimpleSelectNumberField
                      label={t`Professional level`}
                      options={
                        !workedPreviously
                          ? (lists.level[user?.sex ?? 'neutral'] ?? lists.level.neutral)
                              .slice(0, 2)
                              .map((label, value) => ({ value, label }))
                          : (lists.level[user?.sex ?? 'neutral'] ?? lists.level.neutral).map((label, value) => ({
                              value,
                              label,
                            }))
                      }
                      orientation="horizontal"
                    />
                  )}
                />
                <jobForm.AppField
                  name="function"
                  children={(field) => (
                    <field.SelectNumberField
                      label={t`Function`}
                      options={lists.function.map((label, value) => ({ value, label }))}
                      orientation="horizontal"
                    />
                  )}
                />
                <jobForm.AppField
                  name="sector"
                  children={(field) => (
                    <field.SelectNumberField
                      label={t`Sector`}
                      options={lists.sector.map((label, value) => ({ value, label }))}
                      orientation="horizontal"
                    />
                  )}
                />
              </>
            )}
          </jobForm.Subscribe>

          <div className="mt-2 flex">
            <jobForm.AppForm>
              <jobForm.SubmitButton label={t`Save changes`} />
            </jobForm.AppForm>
          </div>
        </form>
      </SettingsSection>

      {/* Employers the person can dissociate from (revoking the company's access to their profile). */}
      <MyEmployersSection />

      <SettingsSection
        title={t`Danger zone`}
        description={t`Irreversible actions`}
        icon={AlertTriangleIcon}
        variant="danger"
        compact
      >
        <DeleteAccount />
      </SettingsSection>
    </div>
  )
}

// Principal administrator: the single admin employee identity the company account can switch into via
// the conmutador. Owner-only surface (UpdateCompany renders only for the real company account). Lets the
// owner reassign it to any administrator employee. Loaded in its own Suspense so it never blocks the form.
function PrincipalAdminSection() {
  const { t } = useLingui()
  const { data: admins } = useSuspenseQuery(Route.useRouteContext().adminEmployeesQueryOptions)
  const { mutateAsync: setPrincipal, isPending } = useMutation(trpc.employees.setPrincipalAdmin.mutationOptions())
  const current = admins.find((a) => a.isPrincipal)?.id ?? null

  const handleChange = async (value: string) => {
    if (value === '' || Number(value) === current) return
    try {
      await setPrincipal({ employeeId: Number(value) })
      toast.success(t`Principal administrator updated`)
    } catch {
      // Surfaced (translated by machine code) via the global tRPC error handler.
    }
  }

  return (
    <SettingsSection
      title={t`Principal administrator`}
      description={t`The employee identity you switch into from the company account`}
      icon={StarIcon}
      compact
    >
      {admins.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          {t`Add an administrator employee first to designate a principal administrator.`}
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          <Select
            value={current == null ? '' : String(current)}
            onValueChange={(value) => value != null && handleChange(value)}
            disabled={isPending}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t`Select an administrator`} />
            </SelectTrigger>
            <SelectContent>
              {admins.map((admin) => (
                <SelectItem key={admin.id} value={String(admin.id)}>
                  <span className="flex items-center gap-2">
                    <Avatar size="sm" type="employee" name={admin.name} url={admin.image} />
                    {admin.name ?? t`Unnamed administrator`}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-muted-foreground text-xs">
            {t`Only this administrator can be reached from the company account via the account switcher.`}
          </p>
        </div>
      )}
    </SettingsSection>
  )
}

function UpdateCompany() {
  const { t } = useLingui()
  const user = useSessionUser()
  const { mutateAsync: updateUser } = useMutation(trpc.auth.updateUser.mutationOptions())
  const lists = useLists()

  const companyForm = useAppForm({
    // Every company field is required (drives the "*" markers and blocks save on empty) except the
    // postal code. Without validators the form saved blank required fields silently.
    validators: {
      onChange: v.object({
        name: v.pipe(v.string(), v.minLength(1)),
        country: v.pipe(v.string(), v.minLength(1)),
        province: v.pipe(v.string(), v.minLength(1)),
        city: v.pipe(v.string(), v.minLength(1)),
        cp: v.string(),
        employees: v.number(),
        sector: v.number(),
      }),
    },
    defaultValues: {
      name: user?.name ?? '',
      country: user?.country ?? '',
      province: user?.province ?? '',
      city: user?.city ?? '',
      cp: user?.cp ?? '',
      employees: user?.employees ?? null,
      sector: user?.sector ?? null,
    },
    onSubmit: async ({ value }) => {
      await updateUser({ ...value, firstname: value.name })
      toast.success(t`Saved successfully`)
    },
  })

  const contactForm = useAppForm({
    validators: {
      onChange: v.object({
        contactFirstname: v.pipe(v.string(), v.minLength(1)),
        contactLastname: v.pipe(v.string(), v.minLength(1)),
        contactPhone: v.pipe(v.string(), v.minLength(1)),
      }),
    },
    defaultValues: {
      contactFirstname: user?.contactFirstname ?? '',
      contactLastname: user?.contactLastname ?? '',
      contactPhone: user?.contactPhone ?? '',
    },
    onSubmit: async ({ value }) => {
      await updateUser(value)
      toast.success(t`Saved successfully`)
    },
  })

  const { mutateAsync: enableWelcome, isPending: enablingWelcome } = useMutation(
    trpc.auth.enableCompanyWelcome.mutationOptions(),
  )

  const handleImageChange = async (file: File) => {
    await updateUser({ image: await fileToBase64(file) })
  }

  const handleEnableWelcome = async () => {
    useAppStore.getState().resetWelcome()
    await enableWelcome()
  }

  return (
    <div className="flex flex-col gap-4">
      <SettingsSection compact className="self-center">
        <ProfileHeaderWithSocial onImageChange={handleImageChange} />
      </SettingsSection>

      <ChangeEmailSection />

      <SettingsSection
        title={t`Company information`}
        description={t`Basic information about your company`}
        icon={BuildingIcon}
        compact
      >
        <form
          onSubmit={(e) => {
            e.preventDefault()
            companyForm.handleSubmit()
          }}
          className="flex flex-col gap-2"
        >
          <companyForm.AppField
            name="name"
            children={(field) => <field.TextField label={t`Company name`} orientation="horizontal" />}
          />
          <companyForm.AppField
            name="country"
            listeners={{ onChange: () => companyForm.setFieldValue('province', '') }}
            children={(field) => <field.CountryField label={t`Country`} orientation="horizontal" />}
          />
          <companyForm.Subscribe selector={(state) => state.values.country}>
            {(country) => (
              <companyForm.AppField
                name="province"
                children={(field) => (
                  <field.ProvinceField label={t`Province / State`} orientation="horizontal" country={country} />
                )}
              />
            )}
          </companyForm.Subscribe>
          <companyForm.AppField
            name="city"
            children={(field) => <field.TextField label={t`City`} orientation="horizontal" />}
          />
          <companyForm.AppField
            name="cp"
            children={(field) => <field.TextField label={t`Postal code`} orientation="horizontal" />}
          />
          <companyForm.AppField
            name="employees"
            children={(field) => (
              <field.SimpleSelectNumberField
                label={t`Number of employees`}
                options={lists.employees.map((label, value) => ({ value, label }))}
                orientation="horizontal"
              />
            )}
          />
          <companyForm.AppField
            name="sector"
            children={(field) => (
              <field.SelectNumberField
                label={t`Sector`}
                options={lists.sector.map((label, value) => ({ value, label }))}
                orientation="horizontal"
              />
            )}
          />

          <div className="mt-2 flex">
            <companyForm.AppForm>
              <companyForm.SubmitButton label={t`Save changes`} />
            </companyForm.AppForm>
          </div>
        </form>
      </SettingsSection>

      <SettingsSection title={t`Contact information`} description={t`How we can reach you`} icon={PhoneIcon} compact>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            contactForm.handleSubmit()
          }}
          className="flex flex-col gap-2"
        >
          <contactForm.AppField
            name="contactFirstname"
            children={(field) => <field.TextField label={t`Contact first name`} orientation="horizontal" />}
          />
          <contactForm.AppField
            name="contactLastname"
            children={(field) => <field.TextField label={t`Contact last name`} orientation="horizontal" />}
          />
          <contactForm.AppField
            name="contactPhone"
            children={(field) => <field.TextField label={t`Contact phone number`} orientation="horizontal" />}
          />

          <div className="mt-2 flex">
            <contactForm.AppForm>
              <contactForm.SubmitButton label={t`Save changes`} />
            </contactForm.AppForm>
          </div>
        </form>
      </SettingsSection>

      <SettingsSection
        title={t`Welcome guide`}
        description={t`Show the onboarding guide again the next time you open the app`}
        icon={SparklesIcon}
        compact
      >
        <div className="flex items-center justify-between gap-4">
          <p className="text-muted-foreground text-sm">
            {t`Bring back the step-by-step guide that explains how to set up Jobs, Teams and Employees.`}
          </p>
          <Button type="button" variant="outline" onClick={handleEnableWelcome} disabled={enablingWelcome}>
            {t`Show welcome guide`}
          </Button>
        </div>
      </SettingsSection>

      {/* Principal administrator (conmutador target) — loaded independently so the admin list never
          blocks the company forms above. */}
      <Suspense fallback={null}>
        <PrincipalAdminSection />
      </Suspense>

      <SettingsSection
        title={t`Danger zone`}
        description={t`Irreversible actions`}
        icon={AlertTriangleIcon}
        variant="danger"
        compact
      >
        <DeleteAccount />
      </SettingsSection>
    </div>
  )
}
